// ============================================================================
// src/lib/projectJobsApi.ts: the one client of the Projects server functions
// ----------------------------------------------------------------------------
// Reads: public.list_project_jobs() returns every job that is not deleted, with
// its people, plus the people who may be picked (the hidden-assignee list is
// applied on the server, D10).
// Writes: public.project_job_operation(p_request_id, p_operation, p_target,
// p_expected, p_change), one audited server entrypoint whose Activity rows are
// written in the same transaction.
//
// WHAT THIS MODULE OWNS: the transport and the meaning of every reply.
//   * REQUEST IDS. One per user action (`newProjectJobRequestId`). A retry of
//     the same action reuses it: the server replays the stored result or
//     applies the operation once, so a lost reply can never create a job twice.
//   * RETRIES. Only a code-less transport or gateway failure is retried, with
//     the SAME id. A database answer (any SQLSTATE) is final.
//   * EXPECTED VALUES. Callers send the value they READ from the loaded job,
//     never a form value; the server compares them under a row lock.
// Mirrors src/lib/sheepAnimalOperationApi.ts (migration 244's client).
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import {newClientSubmissionId} from './clientSubmissionId.js';
import {compressImage} from './photoCompress.js';
import {isISODate} from './projectSchedule.js';
import {TASK_PHOTOS_BUCKET, isStorageDuplicateError} from './tasks.js';

// ── Reads ───────────────────────────────────────────────────────────────────

export type ProjectPerson = {id: string; fullName: string};

export type ProjectPhoto = {
  id: string;
  storagePath: string;
  addedBy: string | null;
  addedByName: string | null;
  addedAt: string | null;
};

export type ProjectJob = {
  id: string;
  title: string;
  notes: string;
  startDate: string | null;
  endDate: string | null;
  completedAt: string | null;
  completedByName: string | null;
  createdAt: string;
  // Profile id of whoever added the job: field roles may edit name/notes and add
  // photos only on jobs they added (D12, D14).
  createdBy: string | null;
  createdByName: string | null;
  updatedAt: string;
  sourceTodoId: string | null;
  assignees: ProjectPerson[];
  assigneeNames: string[];
  photos: ProjectPhoto[];
};

export type ProjectJobsData = {
  jobs: ProjectJob[];
  // People who may be added to a job: active, and not on the hidden list.
  assignable: ProjectPerson[];
};

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);

function readPerson(raw: unknown): ProjectPerson | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.profile_id) ?? str(r.id);
  if (!id) return null;
  return {id, fullName: str(r.full_name)?.trim() || 'Unknown person'};
}

const PHOTO_PATH_RE = new RegExp('^projects/pj-[a-z0-9-]{8,64}/pjp-[a-z0-9-]{8,64}[.]jpg$');

function readPhoto(raw: unknown): ProjectPhoto | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = str(r.id);
  const storagePath = str(r.storage_path);
  // Only a path under this job family is ever signed; anything else is dropped.
  if (!id || !storagePath || !PHOTO_PATH_RE.test(storagePath)) return null;
  return {id, storagePath, addedBy: str(r.added_by), addedByName: str(r.added_by_name), addedAt: str(r.added_at)};
}

function readJob(raw: unknown): ProjectJob {
  if (!raw || typeof raw !== 'object') throw new Error('list_project_jobs: a job row is not an object');
  const r = raw as Record<string, unknown>;
  const id = str(r.id);
  const title = str(r.title);
  const createdAt = str(r.created_at);
  const updatedAt = str(r.updated_at);
  if (!id || title === null || !createdAt || !updatedAt) {
    throw new Error('list_project_jobs: a job row is missing id, title or timestamps');
  }
  const startDate = r.start_date == null ? null : str(r.start_date);
  const endDate = r.end_date == null ? null : str(r.end_date);
  // Fail closed on a half-dated or malformed row rather than drawing a wrong bar.
  if ((startDate === null) !== (endDate === null)) throw new Error(`list_project_jobs: job ${id} is half-dated`);
  if (startDate !== null && (!isISODate(startDate) || !isISODate(endDate) || (endDate as string) < startDate)) {
    throw new Error(`list_project_jobs: job ${id} has invalid dates`);
  }
  const assignees = (Array.isArray(r.assignees) ? r.assignees : [])
    .map(readPerson)
    .filter((p): p is ProjectPerson => p !== null)
    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'en', {sensitivity: 'base'}));
  return {
    id,
    title,
    notes: str(r.notes) ?? '',
    startDate,
    endDate,
    completedAt: r.completed_at == null ? null : str(r.completed_at),
    completedByName: str(r.completed_by_name),
    createdAt,
    createdBy: str(r.created_by),
    createdByName: str(r.created_by_name),
    updatedAt,
    sourceTodoId: str(r.source_todo_id),
    assignees,
    assigneeNames: assignees.map((p) => p.fullName),
    photos: (Array.isArray(r.photos) ? r.photos : []).map(readPhoto).filter((x): x is ProjectPhoto => x !== null),
  };
}

/** Validate the list reply. Anything malformed throws, so the page shows its error banner. */
export function readProjectJobsReply(data: unknown): ProjectJobsData {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('list_project_jobs: the reply is not an object');
  }
  const d = data as Record<string, unknown>;
  if (!Array.isArray(d.jobs) || !Array.isArray(d.assignable)) {
    throw new Error('list_project_jobs: the reply is missing jobs or assignable');
  }
  const assignable = d.assignable
    .map(readPerson)
    .filter((p): p is ProjectPerson => p !== null)
    .sort((a, b) => a.fullName.localeCompare(b.fullName, 'en', {sensitivity: 'base'}));
  return {jobs: d.jobs.map(readJob), assignable};
}

export async function listProjectJobs(sb: SupabaseClient, signal?: AbortSignal): Promise<ProjectJobsData> {
  let call = sb.rpc('list_project_jobs');
  if (signal && typeof (call as {abortSignal?: unknown}).abortSignal === 'function') {
    call = (call as unknown as {abortSignal: (s: AbortSignal) => typeof call}).abortSignal(signal);
  }
  const {data, error} = await call;
  if (error) {
    if (error.code === 'PGRST202' || error.code === '42883') {
      throw new Error('Projects backend is not available on this server.');
    }
    throw new Error(`list_project_jobs: ${error.message}`);
  }
  return readProjectJobsReply(data);
}

// ── Writes ──────────────────────────────────────────────────────────────────

export type ProjectJobOperation =
  | 'job.create'
  | 'job.patch'
  | 'job.schedule'
  | 'job.unschedule'
  | 'job.assign'
  | 'job.complete'
  | 'job.reopen'
  | 'job.remove'
  | 'photo.add'
  | 'photo.remove';

type Json = unknown;

export type ProjectJobOpRequest = {
  requestId: string;
  operation: ProjectJobOperation;
  target: Record<string, never> | {job_id: string};
  expected?: Record<string, Json>;
  change: Record<string, Json>;
};

export type ProjectJobConflict = {field: string; expected: Json; current: Json};

export type ProjectJobOpOutcome =
  | {status: 'saved'; kind: 'created' | 'updated'; jobId: string; eventIds: string[]; replayed: boolean}
  | {status: 'no_change'}
  | {status: 'conflict'; conflicts: ProjectJobConflict[]}
  | {status: 'not_found'}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string};

/** A fresh id for ONE user action. Keep it with the action and reuse it on retry. */
export function newProjectJobRequestId(prefix = 'project-job-'): string {
  return prefix + newClientSubmissionId();
}

/** One new job as the Add Job / Send To Projects form hands it over. */
export type NewProjectJob = {
  requestId: string;
  id: string;
  title: string;
  notes: string;
  startDate: string | null;
  endDate: string | null;
  assigneeIds: string[];
  // Send to Projects (D11): the open To Do this job replaces.
  sourceTodoId: string | null;
};

/** The exact job.create request for a new job (one builder for every caller). */
export function projectJobCreateRequest(job: NewProjectJob): ProjectJobOpRequest {
  return {
    requestId: job.requestId,
    operation: 'job.create',
    target: {},
    expected: {},
    change: {
      id: job.id,
      title: job.title,
      notes: job.notes,
      start_date: job.startDate,
      end_date: job.endDate,
      assignee_ids: job.assigneeIds,
      ...(job.sourceTodoId ? {source_todo_id: job.sourceTodoId} : {}),
    },
  };
}

/** A new job id, minted once per Add Job form so a retried save cannot create two jobs. */
export function newProjectJobId(): string {
  return (
    'pj-' +
    newClientSubmissionId()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
  );
}

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
const SQLSTATE = /^[0-9A-Z]{5}$/;
const RETRYABLE_GATEWAY_STATUS = new Set([0, 502, 503, 504]);

type RpcError = {code?: string; message?: string; status?: number} | null;

function isRetryableTransportError(error: NonNullable<RpcError>): boolean {
  if (typeof error.code === 'string' && error.code.trim() !== '') return false;
  if (typeof error.status === 'number') return RETRYABLE_GATEWAY_STATUS.has(error.status);
  return true;
}

/** Read one reply. Anything that does not answer THIS request is not a receipt. */
export function interpretProjectJobReply(request: ProjectJobOpRequest, data: unknown): ProjectJobOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Json>;
  const unconfirmed: ProjectJobOpOutcome = {
    status: 'unknown',
    message: 'Could not confirm this change was saved. Reload to check.',
  };
  if (d.request_id !== request.requestId || d.operation !== request.operation) return unconfirmed;
  switch (d.kind) {
    case 'created':
    case 'updated': {
      const written = (d.written && typeof d.written === 'object' ? d.written : {}) as Record<string, Json>;
      if (d.ok !== true || typeof written.job_id !== 'string') break;
      return {
        status: 'saved',
        kind: d.kind,
        jobId: written.job_id,
        eventIds: Array.isArray(d.event_ids) ? (d.event_ids as string[]) : [],
        replayed: d.replayed === true,
      };
    }
    case 'no_change':
      return {status: 'no_change'};
    case 'conflict':
      return {status: 'conflict', conflicts: Array.isArray(d.conflicts) ? (d.conflicts as ProjectJobConflict[]) : []};
    case 'not_found':
      return {status: 'not_found'};
  }
  return unconfirmed;
}

/**
 * Apply one operation. Never throws. Transport failures are retried with the
 * same request id; a database or PostgREST answer comes back at once.
 */
export async function applyProjectJobOperation(
  sb: SupabaseClient,
  request: ProjectJobOpRequest,
): Promise<ProjectJobOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('project_job_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change,
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        if (!isRetryableTransportError(error)) {
          return {
            status: 'rejected',
            code: (typeof error.code === 'string' && error.code.trim()) || String(error.status ?? 'ERROR'),
            message: error.message || 'The change was refused.',
          };
        }
        throw new Error(error.message || 'transport');
      }
      return interpretProjectJobReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('project_job_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; try again.'};
}

type FailedOutcome = Exclude<ProjectJobOpOutcome, {status: 'saved'} | {status: 'no_change'}>;

/** The sentence for an outcome that did not save, written for the farm. */
export function projectJobOutcomeMessage(outcome: FailedOutcome): string {
  switch (outcome.status) {
    case 'conflict':
      return 'Someone else changed this job since you opened it. Reload to see the current version.';
    case 'not_found':
      return 'This job was deleted by someone else. Reload to see the current list.';
    case 'rejected':
      switch (outcome.code) {
        case '22023':
          return outcome.message.replace(/^project_job_operation: /, '');
        case '42501':
          return 'You do not have permission to make this change, or your session ended. Sign in again; your change is kept.';
        case 'WCF01':
          return 'This change no longer matches what was saved. Reload and try again.';
        default:
          return outcome.message || 'The change was refused.';
      }
    case 'unknown':
      return outcome.message;
  }
}

/** An operation that did not save, as the error a mutation's caller catches. */
export class ProjectJobOperationError extends Error {
  readonly outcome: FailedOutcome;
  readonly code?: string;
  constructor(outcome: FailedOutcome) {
    super(projectJobOutcomeMessage(outcome));
    this.name = 'ProjectJobOperationError';
    this.outcome = outcome;
    if (outcome.status === 'rejected') this.code = outcome.code;
  }
}

/** Run one operation; resolve on a saved or unchanged outcome, throw otherwise. */
export async function runProjectJobOperation(
  sb: SupabaseClient,
  request: ProjectJobOpRequest,
): Promise<Extract<ProjectJobOpOutcome, {status: 'saved'} | {status: 'no_change'}>> {
  const outcome = await applyProjectJobOperation(sb, request);
  if (outcome.status === 'saved' || outcome.status === 'no_change') return outcome;
  throw new ProjectJobOperationError(outcome);
}

// ── Photos (D13, D14) ───────────────────────────────────────────────────────
// Objects live in the existing private task-photos bucket under
// projects/<jobId>/<photoId>.jpg. The file is uploaded FIRST (compressed,
// upsert false, a duplicate counts as success so a retry is safe), and only
// then does photo.add record it; the server refuses a path with no object.

export function newProjectPhotoId(): string {
  return (
    'pjp-' +
    newClientSubmissionId()
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
  );
}

export function projectPhotoStoragePath(jobId: string, photoId: string): string {
  return `projects/${jobId}/${photoId}.jpg`;
}

export async function uploadProjectPhoto(
  sb: SupabaseClient,
  jobId: string,
  photoId: string,
  file: Blob,
): Promise<string> {
  const path = projectPhotoStoragePath(jobId, photoId);
  const compressed = await compressImage(file);
  const {error} = await sb.storage
    .from(TASK_PHOTOS_BUCKET)
    .upload(path, compressed, {contentType: compressed.type || 'image/jpeg', upsert: false});
  if (error && !isStorageDuplicateError(error)) {
    throw new Error(`The photo did not upload: ${error.message || String(error)}`);
  }
  return path;
}

export async function projectPhotoSignedUrl(
  sb: SupabaseClient,
  storagePath: string,
  ttlSeconds = 600,
): Promise<string> {
  const {data, error} = await sb.storage.from(TASK_PHOTOS_BUCKET).createSignedUrl(storagePath, ttlSeconds);
  if (error) throw new Error(`projectPhotoSignedUrl: ${error.message || String(error)}`);
  return data && data.signedUrl ? data.signedUrl : '';
}
